import { OptionallyVisible } from 'components';
import { FormikErrors, setIn, useFormik } from 'formik';
import { omit } from 'lodash';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useGetCountriesListQuery } from 'services/assets';
import merchantsApi, { useCreateMerchantsBankAccountMutation, useGetTitanDataMutation } from 'services/merchants';
import {
  AccountOwnerType,
  BridgeFiatRail,
  DepositAccountType,
  ExternalAccountType,
  FiatPayoutSettingsPayload,
  MerchantBankInfo,
  MerchantPayoutFlow,
} from 'services/merchants/types';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { FormField } from 'components/form/formField/formField';
import IconAdd from 'components/icons/add';
import { Input } from 'components/inputs';
import { RadioGroup } from 'components/inputs/radio/radioGroup';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { Select, SelectItem } from 'components/inputs/select/select';
import { Buttons } from 'components/modal/styles';

import { APIErrorTypes } from 'types/error';
import { ReduxToolkitError } from 'types/http';
import { NotificationSeverity } from 'types/notifications';

import { flattenKeys, getServerSideValidationDetails, unflattenErrors } from 'utils/error';
import Logger from 'utils/logger';
import { replaceEmptyStringsWithUndefined } from 'utils/string';

import { usePayout } from '../../../payoutModal/context';
import { SetupPayoutStep, usePayoutSetupStep } from '../../../payoutModal/context/modal';
import IncompatibileRail from '../../../payoutModal/fiat/incompatibileRail';
import { LABELS as MAIN_LABELS } from '../../../payoutModal/fiat/keys';
import { ErrorText } from '../../../payoutModal/fiat/styles';
import { KEYS as PAYOUT_MODAL_KEYS } from '../../../payoutModal/keys';
import { Delimiter } from '../../../payoutModal/styles';
import { PayoutType } from '../../../payoutModal/types';
import { LABELS } from '../../keys';
import { Label, Section, SelectContainer, ThinModalButton } from '../form.styles';
import BusinessAccountOwnerForm from './accountOwnerTypes/business';
import IndividualAccountOwnerForm from './accountOwnerTypes/individual';
import {
  ACHFormField,
  ACHFormNestedField,
  ACHFormValues,
  ACHNestedFormValues,
  FORM_FIELDS,
  KEYS,
  NESTED_ACCOUNT_FIELDS,
  accountOwnerTypeOptions,
  depositAccountOptions,
  getPrefilledValues,
  getValidationSchema,
} from './ach.keys';

interface ACHBankAccountFormProps {
  paymentRail: BridgeFiatRail.ACH | BridgeFiatRail.ACH_SAME_DATE | BridgeFiatRail.WIRE;
}

type MerchantBankInfoSelectItem = SelectItem & { value: MerchantBankInfo };

const ACHBankAccountForm = ({ paymentRail }: ACHBankAccountFormProps) => {
  const { t } = useTranslation();
  const payout = usePayout();
  const payoutStep = usePayoutSetupStep();
  const previouslyProvidedValues: any = payout[PayoutType.Fiat][paymentRail]?.form?.values;
  const [isIncompatiblePaymentRail, setIsIncompatiblePaymentRail] = useState(false);
  const { pushToast } = useNotifications();
  const [titanBankAccountSelectionActive, setTitanBankAccountSelectionActive] = useState(false);
  const [payoutSettingsMutation, { isLoading: isLoadingPayoutSettings }] = merchantsApi.useUpdateMerchantPayoutSettingsMutation();

  const [createBankAccount, { isLoading, error }] = useCreateMerchantsBankAccountMutation();
  const [getTitanData, { data: titanData, isLoading: isLoadingTitanData }] = useGetTitanDataMutation();
  const [selectedTitanBankAccount, setSelectedTitanBankAccount] = useState<MerchantBankInfoSelectItem | null>(null);
  const { data: countries = [] } = useGetCountriesListQuery();

  const mappedCountries: SelectItem[] = countries.map((country) => ({
    key: country.isoAlpha3,
    icon: country.iconUrl,
    label: country.name,
  }));

  const backButtonLabel = payoutStep.canGoBack() ? t(LABELS.ACTIONS.BACK) : t(LABELS.ACTIONS.CANCEL);

  const preparePayload = (values: ACHFormValues) => {
    const initialPayload = {
      uuid: payout.merchant.uuid,
      paymentRail: payout[PayoutType.Fiat].paymentRail,
      currency: KEYS.HARDCODED_CURRENCY,
      ...values,
      accountType: ExternalAccountType.US,
    };
    const merchantTypeSpecificFieldsToOmit =
      form.values[FORM_FIELDS.ACCOUNT_OWNER_TYPE] === AccountOwnerType.Business
        ? [FORM_FIELDS.FIRST_NAME, FORM_FIELDS.LAST_NAME]
        : [FORM_FIELDS.BUSINESS_NAME];

    return omit(initialPayload, merchantTypeSpecificFieldsToOmit);
  };

  const form = useFormik<ACHFormValues>({
    initialValues: previouslyProvidedValues ?? getPrefilledValues(),
    validationSchema: getValidationSchema(),
    onSubmit: async (values, formikHelpers) => {
      const errors = await formikHelpers.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      const payload = preparePayload(replaceEmptyStringsWithUndefined(values));

      const bankAccountResponse = await createBankAccount(payload);
      if ('error' in bankAccountResponse) {
        const isIncompatiblePaymentRailError =
          (bankAccountResponse.error as ReduxToolkitError)?.data?.errorType === APIErrorTypes.IncompatiblePaymentRail;

        if (isIncompatiblePaymentRailError) {
          setIsIncompatiblePaymentRail(true);
          return;
        }

        const serverErrors = getServerSideValidationDetails(bankAccountResponse.error, KEYS.UNASSOCIATED_ERROR);
        const touchedFields = flattenKeys(serverErrors).reduce((acc, path) => setIn(acc, path, true), {});

        form.setErrors(unflattenErrors(serverErrors));
        form.setTouched(touchedFields, false);
        return;
      }

      const noNeedToUpdatePaymentRail = payout.merchant.payoutFlow === MerchantPayoutFlow.DIRECT;
      if (bankAccountResponse.data?.data?.uuid) {
        const updatePayoutSettingsPayload: FiatPayoutSettingsPayload = {
          merchantId: payout.merchant.uuid,
          payoutDestinationUuid: bankAccountResponse.data?.data?.uuid,
          payoutMemo: '',
          ...(!noNeedToUpdatePaymentRail && { paymentRail: payout[PayoutType.Fiat].paymentRail }),
        };

        const payoutSettingsResponse = await payoutSettingsMutation(updatePayoutSettingsPayload);
        if ('error' in bankAccountResponse) {
          Logger.error('Unable to update main account after bank account was created:', JSON.stringify(payoutSettingsResponse));
        }
      }

      payoutStep.proceed();
    },
  });
  const hasErrors = Object.values(form.errors).length > 0;

  const onSelectNestedChange = (field: ACHFormField) => (nestedField: ACHFormNestedField) => (value: SelectItem | null) => {
    if (!value?.key) {
      return;
    }

    onFormNestedChange(field)(nestedField)(value.key);
  };

  const onFormChange = (field: ACHFormField) => (value: string) => {
    form.setFieldTouched(field, true);
    form.setFieldValue(field, value);
  };

  const onNestedDontSentEmptyStringChange = (field: ACHFormField) => (nestedField: ACHFormNestedField) => (value: string | undefined) => {
    if (value === '') {
      onFormNestedChange(field)(nestedField)(undefined);
      return;
    }

    onFormNestedChange(field)(nestedField)(value);
  };

  const onFormNestedChange = (field: ACHFormField) => (nestedField: ACHFormNestedField) => (value: string | undefined) => {
    form.setFieldTouched(`${field}.${nestedField}`, true);
    form.setFieldValue(field, {
      ...(form.values[field] as Record<ACHFormNestedField, string | undefined>),
      [nestedField]: value,
    });
  };

  const getCurrentNestedSelectItem =
    (listOfItems: SelectItem[]) =>
    (field: ACHFormField, nestedField: ACHFormNestedField): SelectItem | undefined => {
      return listOfItems.find((item) => item.key === (form.values[field] as any)?.[nestedField]);
    };

  const handleRoutingNumberChange = (value: string) => {
    const normalized = value.replaceAll(' ', '');
    if (normalized.length <= KEYS.ROUTING_NUMBER_MAX_LENGTH) {
      onFormNestedChange(FORM_FIELDS.ACCOUNT)(NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER)(normalized);
    }
  };

  const getNestedFieldError = (field: ACHFormField, nestedField: ACHFormNestedField) => {
    const error = (form.errors[field] as FormikErrors<ACHNestedFormValues>)?.[nestedField];
    const touched = (form.touched[field] as FormikErrors<ACHNestedFormValues>)?.[nestedField];
    if (!error || !touched) {
      return undefined;
    }

    return error;
  };

  const getFieldError = (field: ACHFormField | FormikErrors<ACHNestedFormValues>): string | undefined => {
    if (typeof field === 'object') {
      return undefined;
    }

    if (!form.errors[field]) {
      return undefined;
    }

    if (!form.touched[field]) {
      return undefined;
    }

    return form.errors[field] as string | undefined;
  };

  const storePayoutFormState = () => {
    payout.setPayoutState({
      ...payout,
      [PayoutType.Fiat]: {
        ...payout[PayoutType.Fiat],
        [paymentRail]: {
          ...payout[PayoutType.Fiat][paymentRail],
          form,
        },
      },
    });
  };

  const onGoBack = () => {
    storePayoutFormState();
    payoutStep.goBack();
  };

  const accountOwnerTypeForm =
    {
      [AccountOwnerType.Individual]: <IndividualAccountOwnerForm form={form} onFormChange={onFormChange} getFieldError={getFieldError} />,
      [AccountOwnerType.Business]: <BusinessAccountOwnerForm form={form} onFormChange={onFormChange} getFieldError={getFieldError} />,
    }[form.values[FORM_FIELDS.ACCOUNT_OWNER_TYPE] as any as AccountOwnerType] ?? null;

  const onIncompatiblePaymentRailAcknowledge = () => {
    setIsIncompatiblePaymentRail(false);
  };

  const fillFormValuesWithMerchantBankAccount = (merchantBankAccount: MerchantBankInfo) => {
    form.setValues({
      ...form.values,
      [FORM_FIELDS.ACCOUNT]: {
        [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: merchantBankAccount.account?.accountNumber ?? '',
        [NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]: merchantBankAccount.account?.routingNumber ?? '',
        [NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS]: (merchantBankAccount.account?.checkingOrSavings as DepositAccountType) ?? '',
      },
      [FORM_FIELDS.BUSINESS_NAME]: merchantBankAccount.businessName,
      [FORM_FIELDS.BANK_NAME]: merchantBankAccount.bankName,
      [FORM_FIELDS.ACCOUNT_OWNER_NAME]: merchantBankAccount.accountOwnerName,
      [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: merchantBankAccount.accountOwnerType,
      [FORM_FIELDS.FIRST_NAME]: merchantBankAccount.firstName,
      [FORM_FIELDS.LAST_NAME]: merchantBankAccount.lastName,
      [FORM_FIELDS.ADDRESS]: {
        [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: merchantBankAccount.address?.streetLine1 ?? '',
        [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: merchantBankAccount.address?.streetLine2 ?? '',
        [NESTED_ACCOUNT_FIELDS.CITY]: merchantBankAccount.address?.city ?? '',
        [NESTED_ACCOUNT_FIELDS.STATE]: merchantBankAccount.address?.state ?? '',
        [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: merchantBankAccount.address?.postalCode ?? '',
        [NESTED_ACCOUNT_FIELDS.COUNTRY]: merchantBankAccount.address?.country ?? '',
      },
    });
  };

  const shift4Uuid = payout.merchant?.shift4MerchantId;
  const titanButtonContainer = document.getElementById(PAYOUT_MODAL_KEYS.MODAL_HEADER_ID);
  const showTitanDataInsertButton = payoutStep[PayoutType.Fiat] === SetupPayoutStep.AccountDetails && Boolean(shift4Uuid);

  const onGetTitanDataClick = async () => {
    if (!shift4Uuid) {
      return;
    }

    const response = await getTitanData({ uuid: shift4Uuid });
    if ('error' in response) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.ERRORS.UNABLE_TO_GET_TITAN_DATA),
      });
      return;
    }

    const titanBankAcccounts = response.data?.data?.bankAccounts.filter((bankAccount) =>
      [BridgeFiatRail.ACH, BridgeFiatRail.ACH_SAME_DATE, BridgeFiatRail.WIRE].includes(bankAccount.paymentRail),
    );
    if (!titanBankAcccounts?.length) {
      pushToast({
        severity: NotificationSeverity.info,
        message: t(LABELS.ERRORS.NO_BANK_ACCOUNTS),
      });
      return;
    }

    if (titanBankAcccounts.length === 1) {
      fillFormValuesWithMerchantBankAccount(titanBankAcccounts[0]);

      return;
    }

    setTitanBankAccountSelectionActive(true);
  };

  useEffect(() => {
    storePayoutFormState();
  }, [form.values]);

  if (titanBankAccountSelectionActive) {
    const titanBankAcccounts = titanData?.data?.bankAccounts ?? [];
    const mappedBankAccounts: MerchantBankInfoSelectItem[] = titanBankAcccounts.map((account) => ({
      key: account.uuid,
      label: `${account.bankName} •••• ${account.account?.accountNumber?.replaceAll('*', '')}`,
      value: account,
    }));

    const onBankSelect = (item: SelectItem | null) => {
      setSelectedTitanBankAccount(item as MerchantBankInfoSelectItem | null);
    };

    const onProceedWithExistingAccount = () => {
      fillFormValuesWithMerchantBankAccount(selectedTitanBankAccount?.value as MerchantBankInfo);
      setTitanBankAccountSelectionActive(false);
    };

    const onTitanBankAccountSelectCancel = () => {
      setTitanBankAccountSelectionActive(false);
    };

    return (
      <>
        <Section>
          <Label>{t(MAIN_LABELS.ACCOUNT_SELECTION_LABEL, { currency: payout.merchant.mainFiatCurrency })}</Label>
          <Select<SelectItem, false>
            fullWidth
            value={selectedTitanBankAccount ?? undefined}
            items={mappedBankAccounts}
            onChange={onBankSelect}
            getItemLabel={(item) => item?.label ?? item?.key ?? ''}
            multiple={false}
            closeDropdownOnSelect
            placeholder={t(MAIN_LABELS.ACCOUNT_SELECTION_PLACEHOLDER)}
          />
        </Section>
        <Delimiter />
        <Buttons>
          <Button secondary onClick={onTitanBankAccountSelectCancel}>
            {t(LABELS.ACTIONS.BACK)}
          </Button>
          <Button disabled={!selectedTitanBankAccount} primary onClick={onProceedWithExistingAccount}>
            {t(LABELS.ACTIONS.ADD)}
          </Button>
        </Buttons>
      </>
    );
  }

  if (isIncompatiblePaymentRail) {
    return <IncompatibileRail onIncompatiblePaymentRailAcknowledge={onIncompatiblePaymentRailAcknowledge} />;
  }

  return (
    <>
      <OptionallyVisible visible={Boolean(titanButtonContainer)}>
        {createPortal(
          <OptionallyVisible visible={showTitanDataInsertButton}>
            <ThinModalButton isLoading={isLoadingTitanData} secondary onClick={onGetTitanDataClick}>
              <IconAdd /> {t(MAIN_LABELS.ACTIONS.INSERT_TITAN_DATA)}
            </ThinModalButton>
          </OptionallyVisible>,
          titanButtonContainer || document.body,
        )}
      </OptionallyVisible>
      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_TYPE.TITLE)}</Label>
        <SelectContainer>
          <FormField error={getNestedFieldError(FORM_FIELDS.ACCOUNT, NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS)}>
            <Select<SelectItem>
              value={getCurrentNestedSelectItem(depositAccountOptions)(FORM_FIELDS.ACCOUNT, NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS)}
              items={depositAccountOptions}
              onChange={onSelectNestedChange(FORM_FIELDS.ACCOUNT)(NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS)}
              getItemLabel={(item) => item.label}
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_TYPE)}
              closeDropdownOnSelect
              error={Boolean(getNestedFieldError(FORM_FIELDS.ACCOUNT, NESTED_ACCOUNT_FIELDS.CHECKING_OR_SAVINGS))}
            />
          </FormField>
        </SelectContainer>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.BANK_NAME_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.BANK_NAME)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.BANK_NAME)}
            value={form.values[FORM_FIELDS.BANK_NAME] as string}
            onChange={onFormChange(FORM_FIELDS.BANK_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_NAME_SECTION.TITLE)}</Label>
        <FormField error={getFieldError(FORM_FIELDS.ACCOUNT_OWNER_NAME)}>
          <Input
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_OWNER_NAME)}
            value={form.values[FORM_FIELDS.ACCOUNT_OWNER_NAME]}
            onChange={onFormChange(FORM_FIELDS.ACCOUNT_OWNER_NAME)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_NUMBER_SECTION.TITLE)}</Label>
        <FormField error={getNestedFieldError(FORM_FIELDS.ACCOUNT, NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER)}>
          <Input
            onChange={onFormNestedChange(FORM_FIELDS.ACCOUNT)(NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER)}
            value={form.values[FORM_FIELDS.ACCOUNT][NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ACCOUNT_NUMBER)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ROUTING_NUMBER_SECTION.TITLE)}</Label>
        <FormField error={getNestedFieldError(FORM_FIELDS.ACCOUNT, NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER)}>
          <Input
            onChange={handleRoutingNumberChange}
            value={form.values[FORM_FIELDS.ACCOUNT][NESTED_ACCOUNT_FIELDS.ROUTING_NUMBER]}
            placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.ROUTING_NUMBER)}
          />
        </FormField>
      </Section>

      <Section>
        <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ACCOUNT_OWNER_TYPE.TITLE)}</Label>
        <RadioGroup
          options={accountOwnerTypeOptions}
          onChange={onFormChange(FORM_FIELDS.ACCOUNT_OWNER_TYPE)}
          value={form.values[FORM_FIELDS.ACCOUNT_OWNER_TYPE]}
        />
      </Section>

      {accountOwnerTypeForm}

      <OptionallyVisible visible={Boolean(form.values[FORM_FIELDS.ACCOUNT_OWNER_TYPE])}>
        <Section>
          <Label>{t(LABELS.CREATE_MODAL.SECTIONS.ADDRESS_SECTION.TITLE)}</Label>
          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STREET_LINE_1)}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STREET_LINE_1)}
              value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STREET_LINE_1]}
              onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STREET_LINE_1)}
            />
          </FormField>

          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STREET_LINE_2)}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STREET_LINE_2)}
              value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STREET_LINE_2]}
              onChange={onNestedDontSentEmptyStringChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STREET_LINE_2)}
            />
          </FormField>

          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.CITY)}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.CITY)}
              value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.CITY]}
              onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.CITY)}
            />
          </FormField>

          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.STATE)}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.STATE)}
              value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.STATE]}
              onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.STATE)}
            />
          </FormField>

          <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.POSTAL_CODE)}>
            <Input
              placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.POSTAL_CODE)}
              value={form.values[FORM_FIELDS.ADDRESS][NESTED_ACCOUNT_FIELDS.POSTAL_CODE]}
              onChange={onFormNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.POSTAL_CODE)}
            />
          </FormField>

          <SelectContainer>
            <FormField error={getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.COUNTRY)}>
              <SearchableSelect<SelectItem, false>
                fullWidth
                value={getCurrentNestedSelectItem(mappedCountries)(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.COUNTRY)}
                items={mappedCountries}
                placeholder={t(LABELS.CREATE_MODAL.FORM_FIELDS.PLACEHOLDERS.COUNTRY)}
                onChange={onSelectNestedChange(FORM_FIELDS.ADDRESS)(NESTED_ACCOUNT_FIELDS.COUNTRY)}
                getItemLabel={(item) => item.label}
                multiple={false}
                closeDropdownOnSelect
                error={Boolean(getNestedFieldError(FORM_FIELDS.ADDRESS, NESTED_ACCOUNT_FIELDS.COUNTRY))}
              />
            </FormField>
          </SelectContainer>
        </Section>
      </OptionallyVisible>

      <OptionallyVisible visible={Boolean((error as ReduxToolkitError)?.data?.errorMessage)}>
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage}</ErrorText>
      </OptionallyVisible>

      <Delimiter />
      <Buttons>
        <Button secondary onClick={onGoBack}>
          {backButtonLabel}
        </Button>
        <ButtonWithLoading
          isLoading={isLoading || isLoadingPayoutSettings}
          primary
          onClick={() => form.handleSubmit()}
          disabled={hasErrors}
        >
          {t(LABELS.ACTIONS.ADD)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default ACHBankAccountForm;
